import React, { useState, useEffect } from "react";
import { MasonryGrid, type Photo } from "../components/portfolio/MasonryGrid";
import { ProjectSelector } from "../components/portfolio/ProjectSelector";
import { Lightbox } from "../components/portfolio/Lightbox";
import {
  loadProjects,
  getProjectPhotos,
  getAllPhotos,
  getTopRankedImages
} from "../data/projectLoader";
import { type Project } from "../types/project";
import styles from "./Portfolio.module.css";

export const Portfolio: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Load projects on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const loadedProjects = await loadProjects();
        setProjects(loadedProjects);

        // Load all photos initially
        const allPhotos = await getTopRankedImages();
        setPhotos(allPhotos);
      } catch (error) {
        console.error("Failed to load portfolio data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle project selection
  const handleSelectProject = async (projectName: string | null) => {
    setSelectedProject(projectName);
    setLoading(true);

    try {
      if (projectName === null) {
        // Show all photos
        const allPhotos = await getAllPhotos();
        setPhotos(allPhotos);
      } else {
        // Show specific project
        const projectPhotos = await getProjectPhotos(projectName);
        setPhotos(projectPhotos);
      }
    } catch (error) {
      console.error("Failed to load project photos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoClick = (photo: Photo) => {
    const index = photos.findIndex((p) => p.id === photo.id);
    setCurrentIndex(index);
    setSelectedPhoto(photo);
  };

  const handleClose = () => {
    setSelectedPhoto(null);
  };

  const handleNext = () => {
    if (photos.length === 0) return;
    const nextIndex = (currentIndex + 1) % photos.length;
    setCurrentIndex(nextIndex);
    setSelectedPhoto(photos[nextIndex]);
  };

  const handlePrev = () => {
    if (photos.length === 0) return;
    const prevIndex = (currentIndex - 1 + photos.length) % photos.length;
    setCurrentIndex(prevIndex);
    setSelectedPhoto(photos[prevIndex]);
  };

  if (loading && projects.length === 0) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        <p>Loading portfolio...</p>
      </div>
    );
  }

  return (
    <div className={styles.portfolio}>
      <div className={styles.portfolioHeader}>
        <h1 className={styles.portfolioTitle}>Portfolio</h1>
        <p className={styles.portfolioSubtitle}>
          Exploring moments through the lens
        </p>
      </div>

      <ProjectSelector
        projects={projects}
        selectedProject={selectedProject}
        onSelectProject={handleSelectProject}
      />

      {photos.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No images found in this project.</p>
        </div>
      ) : (
        <div className={styles.portfolioGrid}>
          <MasonryGrid
            photos={photos}
            columns={4}
            staggerDelay={60}
            onPhotoClick={handlePhotoClick}
          />
        </div>
      )}

      <Lightbox
        photo={selectedPhoto}
        onClose={handleClose}
        onNext={handleNext}
        onPrev={handlePrev}
      />
    </div>
  );
};
